// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyInterest } from "@/types";
import { formatDate, pressToOpen, readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    interest: TyInterest;
    /** Opens the editor. The whole card does it, like every other list in the app. */
    onOpen?: () => void;
    onRemove?: () => void;
};

/**
 * One line of what a person is after, inside one enquiry.
 *
 * The lead already says which project it is filed against. This says what they kept *asking* about,
 * which is a different and more useful thing: three of these on one lead is the record of a
 * conversation, and the statuses across them are how somebody picking the deal up cold learns that
 * the villa is dead and the duplex is not.
 *
 * **The status is the design.** It is the only field that moves, the only one anybody scans a column
 * of these for, and the thing the whole sub-schema exists to hold — so it is a coloured rail down
 * the inline edge, a tinted badge at the top, and a dot that matches. Everything else on the card
 * is grey around it.
 *
 * **The whole card opens the editor**, like every list here. It used to carry a preview button that
 * walked off to the unit — one more control to aim at, pointed away from the thing you came to
 * change, and on a wrong URL at that.
 */
export default function InterestCard({ interest, onOpen, onRemove }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const project = typeof interest.project === "object" && interest.project ? readLangValue(interest.project.name, lang) : null;
    const unit = typeof interest.unit === "object" && interest.unit ? interest.unit.number : null;
    const author = typeof interest.client === "object" && interest.client ? interest.client.name : t.crm.sources.catalog;
    const kind = interest.kind ? t.properties.unitTypes[interest.kind] : null;

    /** Most specific first — a line always has something to be called. */
    const title = unit ?? project ?? kind ?? t.crm.notSet;
    /** Said once. Whatever is not the headline becomes the line under it. */
    const under = [unit && project ? project : null, unit || project ? kind : null].filter(Boolean).join(" · ");

    return (
        <article
            className="interest-card"
            data-status={interest.status}
            role={onOpen ? "button" : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onClick={onOpen}
            onKeyDown={onOpen ? pressToOpen(onOpen) : undefined}
        >
            <div className="interest-card__top">
                <span className="interest-card__mark" aria-hidden="true">
                    <Icon name={unit ? "building" : project ? "properties" : "model"} size={16} />
                </span>

                <div className="interest-card__identity">
                    <span className="interest-card__title">{title}</span>
                    {under && <span className="interest-card__under">{under}</span>}
                </div>

                <span className="interest-card__status">
                    <span className="interest-card__dot" aria-hidden="true" />
                    {t.crm.interestStatuses[interest.status]}
                </span>
            </div>

            {interest.note && <p className="interest-card__note">{interest.note}</p>}

            <div className="interest-card__foot">
                <span className="interest-card__by">
                    {author}
                    <span className="interest-card__when">{formatDate(interest.date, lang)}</span>
                </span>

                {onRemove && (
                    <button
                        type="button"
                        className="interest-card__tool"
                        onClick={(event) => {
                            event.stopPropagation();
                            onRemove();
                        }}
                        aria-label={t.common.remove}
                    >
                        <Icon name="trash" size={14} />
                    </button>
                )}
            </div>
        </article>
    );
}
